import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../../hooks/useHandleNurseChange";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { useNurseTimeOff } from "../../../hooks/useNurseTimeOff";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { useScheduleSaves } from "../../../hooks/useScheduleSaves";
import type { ScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import { useShiftTimes } from "../../../hooks/useShiftTimes";
import { useStaffShiftCounts } from "../../../hooks/useStaffShiftCounts";
import { trackEvent } from "../../../utils/analytics";
import { comparePreferences, isShiftInTime, sortByWeekend } from "../../../utils/autoStaffing";
import { scheduleSlotDay } from "../../../utils/departmentWeeks";
import { formatPersonFullName, isNurseActiveInDepartment } from "../../../utils/formatters";
import {
	EmploymentType,
	type Nurse,
	type NurseData,
	NurseFullRole,
	type NurseInShift,
	Preferences,
	type ScheduleData,
	type ShiftType,
} from "../../../utils/models";
import { createDateRange, generateShiftData, isNurseChargeOnDate } from "../../../utils/nursePanelUtils";
import { getNurseChanges } from "../../../utils/utilFunctions";
import type { Direction, MoveNurseData, StaffingData, StaffingDirPredicate } from "./types";
import { getWeekendOnlyNurses, isWeekendShift } from "./utils";

/** No one, until the roster arrives. */
const NO_NURSES: Nurse[] = [];

export function useMoveNursePanelData({
	schedule,
	weekIndex,
	shiftIndex,
	workingDayIndex,
	onClose,
}: {
	/** The panel's schedule weeks; `weekIndex`, `shiftIndex` and `workingDayIndex` are positions in them. */
	schedule: ScheduleWeeks;
	weekIndex: number;
	shiftIndex: number;
	workingDayIndex: number;
	onClose: () => void;
}) {
	const { chosenDepartment } = useChosenDepartment();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: no candidates until the roster arrives; the panel's weeks wait for it with the schedule cache on, and
	// the provider has loaded it with the cache off.
	const nurses = roster ?? NO_NURSES;
	const scheduleData = useMemo<ScheduleData>(() => ({ weeks: schedule.weeks, nurses }), [schedule.weeks, nurses]);
	const { nurseWeekendShifts: getNurseWeekendShifts } = useStaffShiftCounts();
	const { moveNurseToSpecificDate } = useScheduleSaves();
	const showErrorSnackbar = useErrorSnackbar();
	const { getShiftTimes, calculateShiftTimes } = useShiftTimes();
	const timeOffOf = useNurseTimeOff();
	const { moveNurse, isLoading } = useHandleNurseChange();
	const lastNameFirst = useLastNameFirst();
	const { showSnackbar } = useSnackbar();

	const nurseMap = useMemo<Map<number, Nurse>>(() => new Map(scheduleData.nurses.map((nurse) => [nurse.id, nurse])), [scheduleData]);

	const weeklyShifts = scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex];
	const targetShiftType = weeklyShifts.type as ShiftType;
	const workingDate = weeklyShifts.days[workingDayIndex].date;

	const isTargetShiftWeekend = useMemo(() => isWeekendShift(targetShiftType, workingDayIndex), [targetShiftType, workingDayIndex]);

	const [direction, setDirection] = useState<Direction>(() => {
		const dayNursesAmount = weeklyShifts.days[workingDayIndex].nurses.filter((n) => !n.isPendingToDelete).length;

		if (dayNursesAmount >= weeklyShifts.days[workingDayIndex].staffNo) {
			return "from";
		}
		return "to";
	});

	function calculateNursesPreferShiftsInDay(nurses: Nurse[], dayIndex: number) {
		const day = weeklyShifts.days[dayIndex];
		const nursesPreferSet = new Set<number>();
		const nursesNotPreferSet = new Set<number>();

		for (const nurse of nurses) {
			const shiftTimes = calculateShiftTimes(day, shiftIndex, nurse.role);
			const specificPreference = timeOffOf(nurse.id).find((pref) => isShiftInTime(shiftTimes, pref) && pref.data?.toLowerCase() !== "pending_rdo");
			if (specificPreference) {
				if (specificPreference.type === Preferences.Prefer) nursesPreferSet.add(nurse.id);
				if (specificPreference.type === Preferences.Avoid) nursesNotPreferSet.add(nurse.id);
			}
		}
		return { nursesPreferSet, nursesNotPreferSet };
	}

	function calculateNursesPreferShifts(nurses: Nurse[]): { nursesPreferSet: Set<number>; nursesNotPreferSet: Set<number> }[] {
		return weeklyShifts.days.map((day, dayIndex) => {
			return calculateNursesPreferShiftsInDay(nurses, dayIndex);
		});
	}

	const sortRows = (nurses: MoveNurseData[]) => {
		const fullNurses = nurses.map((n) => nurseMap.get(n.nurseData.id) as Nurse);
		const preferences = calculateNursesPreferShifts(fullNurses);

		return nurses.sort((a, b) => {
			const result = sortRowsFunction(a, b, nurseMap, preferences);
			if (result !== 0) {
				const nurse1 = nurseMap.get(a.nurseData.id);
				const nurse2 = nurseMap.get(b.nurseData.id);
			}
			return result;
		});
	};

	const compareChanges = (nurse1: NurseData, nurse2: NurseData) => {
		[];
		if (nurse1.changes !== undefined && nurse2.changes !== undefined) {
			if (nurse1.changes !== nurse2.changes) {
				return nurse1.changes - nurse2.changes;
			}
			const moveWeekIndexA = getNurseChanges(scheduleData, nurse1.id, weekIndex);
			const moveWeekIndexB = getNurseChanges(scheduleData, nurse2.id, weekIndex);

			const moveCountWeekIndexA = Math.max(moveWeekIndexA.additions.length, moveWeekIndexA.removals.length);
			const moveCountWeekIndexB = Math.max(moveWeekIndexB.additions.length, moveWeekIndexB.removals.length);

			if (moveCountWeekIndexA !== moveCountWeekIndexB) {
				return moveCountWeekIndexA - moveCountWeekIndexB;
			}
			return 0;
		}
	};

	const sortRowsFunction = (
		nurse1: MoveNurseData,
		nurse2: MoveNurseData,
		nurseMap: Map<number, Nurse>,
		preferences: {
			nursesPreferSet: Set<number>;
			nursesNotPreferSet: Set<number>;
		}[],
	) => {
		const fullNurse1 = nurseMap.get(nurse1.nurseData.id);
		const fullNurse2 = nurseMap.get(nurse2.nurseData.id);
		if (!fullNurse1 || !fullNurse2) return 0;

		const nurseOccurrencesWeekend: Record<number, number> = {
			[nurse1.nurseData.id]: nurse1.nurseData.weekends as number,
			[nurse2.nurseData.id]: nurse2.nurseData.weekends as number,
		};

		const mergedPerferSet = new Set(
			[
				preferences[nurse1.targetDayIndex].nursesPreferSet.has(fullNurse1.id) ? fullNurse1.id : null,
				preferences[nurse2.targetDayIndex].nursesPreferSet.has(fullNurse2.id) ? fullNurse2.id : null,
			].filter((n): n is number => n !== null),
		);
		const mergedNotPerferSet = new Set(
			[
				preferences[nurse1.targetDayIndex].nursesNotPreferSet.has(fullNurse1.id) ? fullNurse1.id : null,
				preferences[nurse2.targetDayIndex].nursesNotPreferSet.has(fullNurse2.id) ? fullNurse2.id : null,
			].filter((n): n is number => n !== null),
		);

		const preferenceComparison = comparePreferences(fullNurse1, fullNurse2, mergedPerferSet, mergedNotPerferSet);
		if (preferenceComparison !== 0) {
			return preferenceComparison;
		}

		const isNurse1OrigShiftWeekend = isWeekendShift(targetShiftType, nurse1.sourceDayIndex);
		const isNurse2OrigShiftWeekend = isWeekendShift(targetShiftType, nurse2.sourceDayIndex);
		if (isNurse1OrigShiftWeekend !== isNurse2OrigShiftWeekend) {
			if (isTargetShiftWeekend === isNurse1OrigShiftWeekend) return -1;
			if (isTargetShiftWeekend === isNurse2OrigShiftWeekend) return 1;
		}

		const isNurse1TargetShiftWeekend = isWeekendShift(targetShiftType, nurse1.targetDayIndex);
		const isNurse2TargetShiftWeekend = isWeekendShift(targetShiftType, nurse2.targetDayIndex);

		if (isNurse1TargetShiftWeekend !== isNurse2TargetShiftWeekend) {
			if (isTargetShiftWeekend === isNurse1TargetShiftWeekend) return -1;
			if (isTargetShiftWeekend === isNurse2TargetShiftWeekend) return 1;
		}

		// This function will calculate things only if both are mid week to weekends.
		const weekendComparison = sortByWeekend(
			fullNurse1,
			fullNurse2,
			nurseOccurrencesWeekend,
			isNurse1TargetShiftWeekend, // nurse1 and nurse2 weekend are the same
		);
		if (weekendComparison !== null) {
			return weekendComparison;
		}

		const changesComparison = compareChanges(nurse1.nurseData, nurse2.nurseData);
		if (changesComparison) {
			return changesComparison;
		}

		// We use it for the consecutive shifts count
		if (nurse1.nurseData.shifts[nurse1.targetDayIndex].hasWarning || nurse2.nurseData.shifts[nurse2.targetDayIndex].hasWarning) {
			if (nurse1.nurseData.shifts[nurse1.targetDayIndex].hasWarning && !nurse2.nurseData.shifts[nurse2.targetDayIndex].hasWarning) {
				return 1;
			}
			if (!nurse1.nurseData.shifts[nurse1.targetDayIndex].hasWarning && nurse2.nurseData.shifts[nurse2.targetDayIndex].hasWarning) {
				return -1;
			}
		}
		return fullNurse1.id - fullNurse2.id;
	};

	const getNurseIdsForShift = (shift: number, day: number) => {
		return scheduleData.weeks[weekIndex].weeklyShifts[shift].days[day].nurses.filter((n) => !n.isPendingToDelete).map((nurse: NurseInShift) => nurse.nurseId);
	};

	const generateNursesMatrixData = ({
		scheduleData,
		weekIndex,
		sourceDayIndex,
		targetDayIndex,
		shiftIndex,
	}: {
		scheduleData: ScheduleData;
		weekIndex: number;
		sourceDayIndex: number;
		targetDayIndex: number;
		shiftIndex: number;
	}): MoveNurseData[] => {
		const createNurseData = (nurseInfo: Nurse, isSelectable: boolean): NurseData => {
			// Set preferences based on shift constraints if available
			let preferences = "";
			if (nurseInfo.shiftConstraints?.consecutiveAllowedShifts !== undefined) {
				preferences = `Max ${nurseInfo.shiftConstraints.consecutiveAllowedShifts} consecutive shifts`;
			}

			const isCharge = isNurseChargeOnDate(scheduleData, weekIndex, shiftIndex, sourceDayIndex, nurseInfo.id);

			const weekendShifts = getNurseWeekendShifts(nurseInfo.id);
			const nurseChanges = getNurseChanges(scheduleData, nurseInfo.id);
			const changesCount = nurseChanges ? Math.max(nurseChanges.additions.length, nurseChanges.removals.length) : 0;

			const shifts = generateShiftData({
				nurse: nurseInfo as Nurse,
				timeOff: timeOffOf(nurseInfo.id),
				weekIndex,
				hospitalData: scheduleData,
				sourceDayIndex: direction === "to" ? sourceDayIndex : targetDayIndex,
				targetDayIndex,
				chosenDepartment,
			});

			return {
				id: nurseInfo.id,
				// The same nurse can be offered as several move candidates (one per eligible source/target day),
				// so the nurse id alone is NOT a unique grid row id. Scope it by source+target day+shift so each
				// candidate is a distinct, selectable row and the correct one is resolved on Move.
				rowKey: `${nurseInfo.id}:${sourceDayIndex}:${targetDayIndex}:${shiftIndex}`,
				name: formatPersonFullName(nurseInfo, lastNameFirst),
				role: nurseInfo.role || "RN",
				weekends: weekendShifts,
				changes: changesCount,
				preferences,
				...(nurseInfo.nursingStartingDate && {
					nurseStartDate: nurseInfo.nursingStartingDate,
				}),
				shifts,
				charge: isCharge,
				selectable: isSelectable,
				isShiftManager: nurseInfo.isShiftManager,
				trainingStatus: nurseInfo.trainingStatus,
				canBeReliefCharge: nurseInfo.canBeReliefCharge,
			};
		};

		const allNursesFromShift: Nurse[] =
			scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex].days[sourceDayIndex].nurses
				.filter((nurse) => !nurse.isPendingToDelete)
				.map((nurse) => scheduleData.nurses.find((n) => n.id === nurse.nurseId))
				.filter((n): n is Nurse => n !== undefined) || [];

		const preferences = calculateNursesPreferShiftsInDay(allNursesFromShift, targetDayIndex);

		const currentShiftNurseIds = getNurseIdsForShift(shiftIndex, sourceDayIndex);
		const targetShiftNurseIds = getNurseIdsForShift(shiftIndex, targetDayIndex);
		const targetHasLPN = scheduleData.nurses.filter((n) => targetShiftNurseIds.includes(n.id)).some((n) => n.role === NurseFullRole.LPN);
		const weekendOnlyNurses = getWeekendOnlyNurses(scheduleData);

		const originWeekendShift = isWeekendShift(scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex].type as ShiftType, sourceDayIndex);
		const targetWeekendShift = isWeekendShift(scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex].type as ShiftType, targetDayIndex);

		const nursesNotPreferSet = preferences.nursesNotPreferSet;
		const temporaryEmploymentTypes = [EmploymentType.PRN, EmploymentType.PerDiem, EmploymentType.FloatPool, EmploymentType.Travel];

		const nursesFromDay = allNursesFromShift.filter(
			// TODO: missing back to back shifts
			(nurse: Nurse) =>
				(chosenDepartment?.department_id !== "Auto_Scheduler" ||
					((nurse.role === NurseFullRole.RN || !targetHasLPN) && // We can't have more then one LPN per shift
						!temporaryEmploymentTypes.includes(nurse.employmentType) &&
						(originWeekendShift === targetWeekendShift || !weekendOnlyNurses.has(nurse.id)) && // We can't move weekend only nurse to weekday
						!nurse.isShiftManager)) &&
				currentShiftNurseIds.includes(nurse.id) &&
				!targetShiftNurseIds.includes(nurse.id) &&
				isNurseActiveInDepartment(nurse, chosenDepartment.department_id) &&
				!nursesNotPreferSet.has(nurse.id),
		);

		const sortedNurseIds = new Set(nursesFromDay.map((nurse) => nurse.id));
		const allNurses = nursesFromDay.concat(allNursesFromShift.filter((n) => !nursesFromDay.some((n2) => n2.id === n?.id)));

		const result = allNurses.map((nurse) => ({
			nurseData: createNurseData(nurse, sortedNurseIds.has(nurse.id)),
			sourceDayIndex,
			targetDayIndex,
		}));

		return result;
	};

	// Keyed by the grid ROW key (nurse id is ambiguous across duplicate candidates), so Move resolves the exact
	// source/target-day candidate the user picked.
	const [selectedRowKey, setSelectedRowKey] = useState<string>();

	const dateRange = useMemo(() => createDateRange(workingDate), [workingDate]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the rows read each nurse's time off through generateNursesMatrixData, so a time-off change must rebuild them.
	const nursesData = useMemo<MoveNurseData[]>(() => {
		const weeklyShifts = scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex];
		const workingDayNursesAmount = weeklyShifts.days[workingDayIndex].nurses.filter((n) => !n.isPendingToDelete).length;

		const getDailyNurseData = ({ toPredicate, fromPredicate }: { toPredicate: StaffingDirPredicate; fromPredicate: StaffingDirPredicate }): MoveNurseData[] => {
			return weeklyShifts.days.flatMap<MoveNurseData>((day, dayIndex) => {
				if (dayIndex === workingDayIndex) return [];

				const dayNursesAmount = day.nurses.filter((n) => !n.isPendingToDelete).length;

				if (direction === "to") {
					const shouldAddToShifts = toPredicate({
						dayNursesAmount,
						dayRequiredNursesAmount: day.staffNo,
						targetDayNursesAmount: workingDayNursesAmount,
					});
					if (shouldAddToShifts) {
						return generateNursesMatrixData({
							scheduleData,
							weekIndex,
							sourceDayIndex: dayIndex,
							targetDayIndex: workingDayIndex,
							shiftIndex,
						});
					}
				} else {
					const shouldAddFromShifts = fromPredicate({
						dayNursesAmount,
						dayRequiredNursesAmount: day.staffNo,
						targetDayNursesAmount: workingDayNursesAmount,
					});
					if (shouldAddFromShifts) {
						return generateNursesMatrixData({
							scheduleData,
							weekIndex,
							sourceDayIndex: workingDayIndex,
							targetDayIndex: dayIndex,
							shiftIndex,
						});
					}
				}
				return [];
			});
		};

		let finalNurseData: MoveNurseData[] = getDailyNurseData({
			toPredicate: ({ dayNursesAmount, dayRequiredNursesAmount, targetDayNursesAmount }) => {
				return dayNursesAmount > dayRequiredNursesAmount && dayNursesAmount > targetDayNursesAmount + 1;
			},
			fromPredicate: ({ dayNursesAmount, dayRequiredNursesAmount, targetDayNursesAmount }) => {
				return dayNursesAmount < dayRequiredNursesAmount && dayNursesAmount < targetDayNursesAmount - 1;
			},
		});

		/** No overstaffed shifts this week */

		if (!finalNurseData.length) {
			finalNurseData = getDailyNurseData({
				toPredicate: ({ dayNursesAmount, targetDayNursesAmount }) => {
					return dayNursesAmount > targetDayNursesAmount + 1;
				},
				fromPredicate: ({ dayNursesAmount, targetDayNursesAmount }) => {
					return dayNursesAmount < targetDayNursesAmount - 1;
				},
			});
		}

		const selectableResults = sortRows([...finalNurseData.filter((n) => n.nurseData.selectable)]);
		return [...selectableResults, ...finalNurseData.filter((n) => !n.nurseData.selectable)];
	}, [scheduleData, timeOffOf, weekIndex, shiftIndex, workingDayIndex, direction]);

	const tableNursesData = useMemo(() => nursesData.map((n) => n.nurseData), [nursesData]);

	const getStaffingData = (): StaffingData => {
		const localWeekIndex = weekIndex;
		const dayShiftNo = 0; // Assuming day shift is index 0
		const nightShiftNo = 1; // Assuming night shift is index 1

		// Initialize default arrays with distinct objects
		const dayShiftData = Array(7)
			.fill(null)
			.map(() => ({ nurses: 0, staffNo: 0 }));
		const nightShiftData = Array(7)
			.fill(null)
			.map(() => ({ nurses: 0, staffNo: 0 }));

		// Process both shifts in a single loop to avoid code duplication
		[
			{ shiftNo: dayShiftNo, dataArray: dayShiftData },
			{ shiftNo: nightShiftNo, dataArray: nightShiftData },
		].forEach(({ shiftNo, dataArray }) => {
			const shift = scheduleData?.weeks[localWeekIndex]?.weeklyShifts[shiftNo];

			if (shift?.days) {
				shift.days.forEach((day, dayIndex) => {
					if (dayIndex < 7) {
						// Ensure we stay within bounds
						// Get number of nurses excluding pending deletions
						const nursesCount = (day.nurses || []).filter((nurse) => !nurse.isPendingToDelete).length;

						dataArray[dayIndex] = {
							nurses: nursesCount,
							staffNo: day.staffNo || 0,
						};
					}
				});
			}
		});

		return {
			dayShift: dayShiftData,
			nightShift: nightShiftData,
		};
	};

	const staffingData = getStaffingData();

	const handleMove = async () => {
		if (isLoading.moveNurse) return;

		if (selectedRowKey === undefined) {
			// No nurse selected, can't proceed
			onClose();
			return;
		}

		// Look up by the unique row key (the grid sorts and the same nurse can appear as multiple candidates,
		// so neither a positional index nor the nurse id alone would resolve the right one).
		const selectedNurse = nursesData.find((n) => n.nurseData.rowKey === selectedRowKey)?.nurseData;
		if (!selectedNurse) return;
		const dayIndex = selectedNurse?.shifts?.find((s) => s.originalDayIndex !== undefined)?.originalDayIndex as number;
		const toIndex = dateRange.findIndex((date) => date.toFormat("yyyy-MM-dd") === workingDate.toFormat("yyyy-MM-dd"));

		const dayNo = direction === "to" ? dayIndex : toIndex;
		const otherDayNo = direction === "to" ? toIndex : dayIndex;

		try {
			const weeklyShifts = scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex];
			const nurseToRemove = weeklyShifts.days[dayNo].nurses.find((nurse) => nurse.nurseId === selectedNurse.id);
			if (!nurseToRemove) throw new Error(`Staff member ${selectedNurse.id} has no shift on the day being moved from`);
			const otherShiftTimes = getShiftTimes(weeklyShifts.days[otherDayNo], shiftIndex, selectedNurse.id);
			const from = scheduleSlotDay(scheduleData.weeks, { weekIndex, shiftIndex, dayIndex: dayNo });
			const to = scheduleSlotDay(scheduleData.weeks, { weekIndex, shiftIndex, dayIndex: otherDayNo });

			const newNurseId: string = await moveNurse({
				nurse: nurseToRemove,
				...from,
				otherDate: to.date,
				otherShiftType: to.shiftType,
				otherShiftTimes,
			});

			moveNurseToSpecificDate(from.date, from.shiftType, to.date, to.shiftType, selectedNurse.id, newNurseId);
			showSnackbar(
				`${nurseToRemove?.name} was successfully moved from  ${nurseToRemove?.startTime.toFormat("ccc")} ${nurseToRemove?.shiftType}
                To ${otherShiftTimes?.start?.toFormat("ccc")} ${scheduleData.weeks[weekIndex].weeklyShifts[shiftIndex].type} shift`,
				"success",
			);
		} catch (e) {
			showErrorSnackbar("Failed to move nurse", { nurseId: selectedNurse.id }, e);
			return;
		}

		trackEvent("Approve Move dialog", { nurse: selectedNurse });
		// Close the dialog
		onClose();
	};

	return {
		staffingData,
		nursesData,
		direction,
		setDirection,
		workingDate,
		targetShiftType,
		tableNursesData,
		dateRange,
		handleMove,
		selectedRowKey,
		setSelectedRowKey,
		isLoading,
	};
}
